import { floatingMenuHTML, styleTemplate } from './template'
import {
  saveFloatingPosition,
  getFloatingPosition,
  clearFloatingPosition
} from '../utils/floatingMenuStorage'
import { throttle } from '@/utils'

export interface FloatingMenuItem {
  icon?: string
  text?: string
  action?: string
  divider?: boolean
}

export interface FloatingMenuConfig {
  position?: {
    bottom?: string
    right?: string
  }
  items?: FloatingMenuItem[]
  onItemClick?: (item: FloatingMenuItem) => void
}

export class FloatingMenu {
  private container: HTMLDivElement
  private config: FloatingMenuConfig
  private styleElement: HTMLStyleElement | null = null
  private unbindHandlers: (() => void) | null = null
  private resizeHandler: () => void
  // 保存位置的百分比
  private positionPercent: { x: number; y: number } | null = null
  private badgeCount = 0
  private isDragging = false // 提取拖拽状态

  constructor(config: FloatingMenuConfig = {}) {
    this.config = config
    this.initStyleContext()
    this.container = this.initInnerHTML()
    this.unbindHandlers = this.initDraggable()

    this.resizeHandler = throttle(() => this.onResize(), 100)
    this.initResizeListener()

    // 初始化位置
    this.initPosition()

    // 初始化菜单项
    if (config.items) {
      this.setItems(config.items)
    } else {
      this.initEvent()
    }
  }

  // 初始化位置
  private initPosition(): void {
    const savedPosition = getFloatingPosition()
    if (savedPosition) {
      console.log(`位置: left=${savedPosition.left}, top=${savedPosition.top}`)
      this.setPositionByCoords(savedPosition.left, savedPosition.top)
    } else if (this.config.position) {
      this.setPositionByStyle(this.config.position)
    }
    // 计算并保存初始百分比
    this.updatePositionPercent()
  }

  // refreshTemplate 方法
  public refreshTemplate(): void {
    // 保存当前状态
    const currentBadgeCount = this.badgeCount
    const currentItems = this.config.items
    const currentPosition = {
      left: this.container.offsetLeft,
      top: this.container.offsetTop
    }

    // 移除旧的 DOM 和事件监听
    this.unbindHandlers?.()

    // 重新生成 HTML
    this.container.innerHTML = floatingMenuHTML()

    // 重新初始化拖拽
    this.unbindHandlers = this.initDraggable()

    // 恢复位置
    this.setPositionByCoords(currentPosition.left, currentPosition.top)

    // 恢复菜单项
    if (currentItems) {
      this.setItems(currentItems)
    } else {
      this.initEvent()
    }

    // 恢复徽章数
    this.setBadgeCount(currentBadgeCount)

    console.log('模板已刷新')
  }

  private static readonly DRAG_THRESHOLD = 5 // 拖拽阈值

  private clampPosition(left: number, top: number): { left: number; top: number } {
    const maxX = window.innerWidth - this.container.offsetWidth
    const maxY = window.innerHeight - this.container.offsetHeight

    return {
      left: Math.max(0, Math.min(left, maxX)),
      top: Math.max(0, Math.min(top, maxY))
    }
  }

  private getContainerPosition(): { left: number; top: number } {
    const rect = this.container.getBoundingClientRect()
    return {
      left: rect.left,
      top: rect.top
    }
  }

  private applyPosition(left: number, top: number): void {
    const clamped = this.clampPosition(left, top)

    this.container.style.left = `${clamped.left}px`
    this.container.style.top = `${clamped.top}px`
    this.container.style.right = 'auto'
    this.container.style.bottom = 'auto'
  }

  // 简化拖拽逻辑
  private initDraggable() {
    let startX: number, startY: number
    let initialLeft: number, initialTop: number
    let hasMoved = false

    const handleDragStart = (clientX: number, clientY: number, target: EventTarget | null): boolean => {
      if ((target as Element)?.closest('.floating-menu-item')) {
        return false
      }

      this.isDragging = true
      hasMoved = false
      startX = clientX
      startY = clientY

      const pos = this.getContainerPosition()
      initialLeft = pos.left
      initialTop = pos.top

      this.container.classList.add('dragging')
      return true
    }

    const handleDragMove = (clientX: number, clientY: number): void => {
      if (!this.isDragging) return

      const deltaX = clientX - startX
      const deltaY = clientY - startY

      // 判断是否超过拖拽阈值
      if (Math.abs(deltaX) > FloatingMenu.DRAG_THRESHOLD ||
        Math.abs(deltaY) > FloatingMenu.DRAG_THRESHOLD) {
        hasMoved = true
      }

      const newLeft = initialLeft + deltaX
      const newTop = initialTop + deltaY

      this.applyPosition(newLeft, newTop)

      const clamped = this.clampPosition(newLeft, newTop)
      saveFloatingPosition(clamped.left, clamped.top)
    }

    const handleDragEnd = (preventDefault: () => void, stopPropagation: () => void): void => {
      if (!this.isDragging) return

      this.isDragging = false
      this.container.classList.remove('dragging')

      if (hasMoved) {
        preventDefault()
        stopPropagation()
        this.updatePositionPercent()
      }
    }

    // 鼠标事件
    const onMouseDown = (e: MouseEvent) => {
      if (handleDragStart(e.clientX, e.clientY, e.target)) {
        e.preventDefault()
        e.stopPropagation()
      }
    }

    const onMouseMove = (e: MouseEvent) => {
      handleDragMove(e.clientX, e.clientY)
    }

    const onMouseUp = (e: MouseEvent) => {
      handleDragEnd(() => e.preventDefault(), () => e.stopPropagation())
    }

    // 触摸事件
    const onTouchStart = (e: TouchEvent) => {
      const touch = e.touches[0]
      handleDragStart(touch.clientX, touch.clientY, e.target)
    }

    const onTouchMove = (e: TouchEvent) => {
      if (!this.isDragging) return

      const touch = e.touches[0]
      const deltaX = touch.clientX - startX
      const deltaY = touch.clientY - startY

      if (Math.abs(deltaX) > FloatingMenu.DRAG_THRESHOLD ||
        Math.abs(deltaY) > FloatingMenu.DRAG_THRESHOLD) {
        hasMoved = true
        e.preventDefault() // 防止页面滚动
      }

      handleDragMove(touch.clientX, touch.clientY)
    }

    const onTouchEnd = (e: TouchEvent) => {
      handleDragEnd(() => e.preventDefault(), () => e.stopPropagation())
    }

    // 绑定事件
    this.container.addEventListener('mousedown', onMouseDown)
    document.addEventListener('mousemove', onMouseMove)
    document.addEventListener('mouseup', onMouseUp)

    this.container.addEventListener('touchstart', onTouchStart, { passive: false })
    document.addEventListener('touchmove', onTouchMove, { passive: false })
    document.addEventListener('touchend', onTouchEnd)

    // 返回清理函数
    return () => {
      this.container.removeEventListener('mousedown', onMouseDown)
      document.removeEventListener('mousemove', onMouseMove)
      document.removeEventListener('mouseup', onMouseUp)
      this.container.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchmove', onTouchMove)
      document.removeEventListener('touchend', onTouchEnd)
    }
  }

  private initStyleContext(): void {
    if (document.querySelector('#floating-menu-style')) {
      return
    }

    this.styleElement = document.createElement('style')
    this.styleElement.id = 'floating-menu-style'
    this.styleElement.textContent = styleTemplate
    document.head.appendChild(this.styleElement)
  }

  private initInnerHTML(): HTMLDivElement {
    const container = document.createElement('div')
    container.className = 'floating-menu-container'
    container.innerHTML = floatingMenuHTML()

    document.body.appendChild(container)

    return container
  }

  public restorePosition(): void {
    clearFloatingPosition()
    // 🔧 重构：恢复到默认位置
    if (this.config.position) {
      this.setPositionByStyle(this.config.position)
    }
    this.updatePositionPercent()
  }

  // 更新位置百分比
  private updatePositionPercent(): void {
    const rect = this.container.getBoundingClientRect()
    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight

    // 计算中心点的百分比位置
    const centerX = rect.left + rect.width / 2
    const centerY = rect.top + rect.height / 2

    this.positionPercent = {
      x: centerX / windowWidth,
      y: centerY / windowHeight
    }

    console.log('更新百分比位置:', this.positionPercent)
  }

  // 根据百分比恢复位置
  private restorePositionByPercent(): void {
    if (!this.positionPercent) return

    const windowWidth = window.innerWidth
    const windowHeight = window.innerHeight
    const containerWidth = this.container.offsetWidth
    const containerHeight = this.container.offsetHeight

    // 根据百分比计算中心点位置
    const centerX = windowWidth * this.positionPercent.x
    const centerY = windowHeight * this.positionPercent.y

    // 计算左上角位置
    const left = centerX - containerWidth / 2
    const top = centerY - containerHeight / 2

    this.applyPosition(left, top)

    // 保存新位置
    const clamped = this.clampPosition(left, top)
    saveFloatingPosition(clamped.left, clamped.top)

    console.log('按百分比恢复位置:', clamped)
  }

  private onResize(): void {
    this.restorePositionByPercent()
  }

  private initResizeListener(): void {
    window.addEventListener('resize', this.resizeHandler)
  }

  private removeResizeListener(): void {
    window.removeEventListener('resize', this.resizeHandler)
  }

  // 提取菜单项点击处理
  private handleItemClick(itemElement: HTMLElement): void {
    const action = itemElement.getAttribute('data-action')
    const iconHTML =
      itemElement.querySelector('.floating-menu-item-icon')?.innerHTML || ''
    const text =
      itemElement.querySelector('.floating-menu-item-text')?.textContent || ''

    const item: FloatingMenuItem = {
      action: action || undefined,
      icon: iconHTML,
      text: text
    }

    this.config.onItemClick?.(item)
  }

  private initEvent(): void {
    const menuItems = this.container.querySelectorAll(
      '.floating-menu-item'
    ) as NodeListOf<HTMLElement>

    menuItems.forEach((itemElement) => {
      itemElement.addEventListener('click', () => {
        this.handleItemClick(itemElement)
      })
    })
  }

  // 提取菜单项渲染逻辑
  private renderMenuItem(item: FloatingMenuItem): string {
    if (item.divider) {
      return '<div class="floating-menu-divider"></div>'
    }
    return `
      <div class="floating-menu-item" data-action="${item.action || ''}">
        <div class="floating-menu-item-icon">${item.icon || ''}</div>
        <span class="floating-menu-item-text">${item.text || ''}</span>
      </div>
    `
  }

  setItems(items: FloatingMenuItem[]): void {
    const panel = this.container.querySelector('.floating-menu-panel')
    if (!panel) return

    panel.innerHTML = items.map(item => this.renderMenuItem(item)).join('')

    this.config.items = items

    this.initEvent()
  }

  public setBadgeCount(count: number): void {
    const badge = this.container.querySelector<HTMLElement>(
      '#floating-menu-badge'
    )
    if (!badge) return

    badge.textContent = count === 0 ? '' : count > 9 ? '9+' : count.toString()
    this.badgeCount = count
  }

  public addBadgeCount(): void {
    this.setBadgeCount(this.badgeCount + 1)
  }

  public getBadgeCount(): number {
    return this.badgeCount
  }

  // 通过样式属性设置位置（bottom, right）
  private setPositionByStyle(position: {
    bottom?: string
    right?: string
  }): void {
    if (position.bottom) {
      this.container.style.bottom = position.bottom
      this.container.style.top = 'auto'
    }
    if (position.right) {
      this.container.style.right = position.right
      this.container.style.left = 'auto'
    }
  }

  // 通过坐标设置位置（left, top）
  private setPositionByCoords(left: number, top: number): void {
    this.applyPosition(left, top)
  }

  hide(): void {
    this.container.style.display = 'none'
  }

  show(): void {
    this.container.style.display = 'block'
  }

  destroy(): void {
    this.container.remove()

    if (this.styleElement) {
      this.styleElement.remove()
    }
    this.unbindHandlers?.()
    this.removeResizeListener()
  }

  setOnItemClick(callback: (item: FloatingMenuItem) => void): void {
    this.config.onItemClick = callback
  }
}
